import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { InputNumber, Switch, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { fetchActionCatalog } from '../../../../../redux/actions/trm/actionCatalog';
import style from './ScoringOverrides.scss';

const InfoIcon = ({ title }) => (
  <Tooltip title={title}>
    <InfoCircleOutlined className={style['info-icon']} />
  </Tooltip>
);
InfoIcon.propTypes = { title: PropTypes.node.isRequired };

const TIPS = {
  scoreOverride: (
    <>
      Per-rule score (0–100). Wins over scenario defaults at runtime. Leave
      blank to inherit the catalog default for this rule&apos;s action on save.
    </>
  ),
  hardRule: (
    <>
      Forces this rule&apos;s score and the alert score to 100 (level High)
      regardless of aggregation mode or deviation modifier. Use only for
      circuit-breaker rules.
    </>
  )
};

const ScoringOverrides = ({
  params,
  onChange,
  disabled,
  matchAction,
  catalogRows,
  fetchActionCatalogAction
}) => {
  // Pre-load catalog once per drawer mount so placeholder + helper text resolve
  // even if the user opens this tab before TypologiesTab dispatched its fetch.
  useEffect(() => {
    if (typeof fetchActionCatalogAction === 'function') {
      fetchActionCatalogAction();
    }
  }, [fetchActionCatalogAction]);

  const value = params || { scoreOverride: null, hardRule: false };

  const [localValue, setLocalValue] = useState(
    value.scoreOverride === null || value.scoreOverride === undefined
      ? null
      : value.scoreOverride
  );

  // Re-sync local state when canonical parent value changes (fresh rule loads).
  useEffect(() => {
    setLocalValue(
      value.scoreOverride === null || value.scoreOverride === undefined
        ? null
        : value.scoreOverride
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.scoreOverride]);

  const overrideError = useMemo(() => {
    if (localValue === null || localValue === undefined) return null;
    if (typeof localValue !== 'number' || Number.isNaN(localValue))
      return 'must be a valid number';
    if (localValue < 0 || localValue > 100) return 'must be between 0 and 100';
    return null;
  }, [localValue]);

  const update = (patch) => onChange({ ...value, ...patch });

  const catalogWeight = useMemo(() => {
    if (!matchAction || !Array.isArray(catalogRows)) return undefined;
    const row = catalogRows.find((r) => r && r.displayName === matchAction);
    return row ? row.weight : undefined;
  }, [matchAction, catalogRows]);

  // FIN-13286: a hard rule always scores 100, so its Score Override is locked
  // (and cleared when Hard rule is switched on).
  const isHard = !!value.hardRule;

  const placeholder = isHard
    ? '100'
    : catalogWeight !== undefined && catalogWeight !== null
    ? String(catalogWeight)
    : 'Enter a score 0–100';

  const onScoreChange = (n) => {
    setLocalValue(n === null || n === undefined ? null : n);
    if (n === null || n === undefined) {
      update({ scoreOverride: null });
      return;
    }
    if (typeof n === 'number' && !Number.isNaN(n)) {
      update({ scoreOverride: n });
    }
  };

  const onHardRuleChange = (v) => {
    if (v) {
      setLocalValue(null);
      update({ hardRule: true, scoreOverride: null });
    } else {
      update({ hardRule: false });
    }
  };

  return (
    <div className={style['scoring-overrides']}>
      <div className={style.section}>
        <label htmlFor="scoreOverride" className={style.label}>
          <span>Score Override</span>
          <InfoIcon title={TIPS.scoreOverride} />
        </label>
        <InputNumber
          id="scoreOverride"
          aria-label="Score Override"
          min={0}
          max={100}
          step={0.5}
          value={isHard ? null : localValue}
          onChange={onScoreChange}
          disabled={disabled || isHard}
          placeholder={placeholder}
          className={style.input}
        />
        {!isHard && overrideError && (
          <div role="alert" className={style.error}>
            {overrideError}
          </div>
        )}
        {isHard ? (
          <div className={style.help} data-testid="hard-rule-score-hint">
            Hard rules always score 100; Score Override is not used.
          </div>
        ) : catalogWeight !== undefined && catalogWeight !== null ? (
          <div className={style.help} data-testid="catalog-default-hint">
            {`Catalog default: ${catalogWeight}. Leave blank to use it on save.`}
          </div>
        ) : (
          <div className={style.help}>Score (0–100) for this rule.</div>
        )}
      </div>

      <div
        className={`${style.section} ${style['hard-rule']} ${
          isHard ? style['is-on'] : ''
        }`}
      >
        <label className={style.label}>
          <span>Hard rule</span>
          <InfoIcon title={TIPS.hardRule} />
        </label>
        <div className={style['switch-row']}>
          <Switch
            id="hardRule"
            aria-label="Hard rule"
            checked={isHard}
            onChange={onHardRuleChange}
            disabled={disabled}
          />
          <label htmlFor="hardRule" className={style['switch-label']}>
            {isHard ? 'Enabled — circuit breaker active' : 'Disabled'}
          </label>
        </div>
        <div className={style.help}>
          Hard rules score 100 and bypass aggregation + deviation modifier.
        </div>
      </div>
    </div>
  );
};

ScoringOverrides.propTypes = {
  params: PropTypes.shape({
    scoreOverride: PropTypes.number,
    hardRule: PropTypes.bool
  }),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  matchAction: PropTypes.string,
  catalogRows: PropTypes.array,
  fetchActionCatalogAction: PropTypes.func
};
ScoringOverrides.defaultProps = {
  params: null,
  disabled: false,
  matchAction: null,
  catalogRows: [],
  fetchActionCatalogAction: undefined
};

const EMPTY_CATALOG_ROWS = [];

const mapStateToProps = (state) => ({
  catalogRows: state.actionCatalog?.rows ?? EMPTY_CATALOG_ROWS
});

const mapDispatchToProps = {
  fetchActionCatalogAction: fetchActionCatalog
};

export { ScoringOverrides as ScoringOverridesPresentational };

export default connect(mapStateToProps, mapDispatchToProps)(ScoringOverrides);
